/**
 * "Scope with an agent" (PLAN G5): the scoping conversation next to the proposal it produces.
 * Left: the discuss conversation with the triage agent on the scoping machine (research allowed,
 * saved with the project). Right: reviewable cards — goal and milestones, the shared config with
 * every choice explained (learned vs proposed), lanes, first tasks and, for an existing repo, the
 * normalizations the audit suggests. Include/exclude each card, pick machines, then Create: one
 * passkey checks the project out and commits exactly the reviewed config. Centered dialog.
 */
import { useQueryClient } from '@tanstack/react-query'
import { Laptop, Sparkles } from 'lucide-react'
import { useId, useMemo, useState } from 'react'
import { type CheckoutProgress, checkoutOk } from '../../lib/account-projects.js'
import { useBackend } from '../../lib/backend.js'
import {
  acceptSetup,
  buildSetupCards,
  repoChoiceOf,
  type SetupCard,
} from '../../lib/project-setup.js'
import { qk, useMachines, useProject } from '../../lib/queries.js'
import { useBark } from '../bark/useBarks.js'
import { Conversation } from '../discuss/Conversation.js'
import { CheckoutRows } from '../NewProjectDialog.js'
import { Button, errorText, Sheet } from '../ui.js'
import { TendencyList } from './TendencyList.js'
import { useAcceptDeps, useTendencies } from './useProjectSetup.js'

const KIND_LABEL: Record<SetupCard['kind'], string> = {
  goal: 'Goal',
  config: 'Shared config',
  lane: 'Lane',
  task: 'First task',
  normalize: 'Normalize',
}

function Evidence({ quote }: { quote?: string }) {
  if (!quote) return null
  return <p className="m-0 text-[14px] text-text-3">From the conversation: “{quote}”</p>
}

export function SetupCardView({
  card,
  included,
  onToggle,
  disabled,
}: {
  card: SetupCard
  included: boolean
  onToggle: (v: boolean) => void
  disabled?: boolean
}) {
  const id = useId()
  return (
    <li
      data-setup-card={card.id}
      className={`grid gap-1.5 rounded-xl border p-3 ${
        included
          ? 'border-border-subtle bg-surface-1'
          : 'border-border-subtle bg-surface-2 opacity-70'
      }`}
    >
      <label htmlFor={id} className="flex items-baseline gap-2">
        <input
          id={id}
          type="checkbox"
          checked={included}
          disabled={disabled || card.kind === 'config'}
          onChange={(e) => onToggle(e.target.checked)}
        />
        <span className="text-[14px] font-semibold text-text-2">{KIND_LABEL[card.kind]}</span>
        <span className="min-w-0 flex-1 break-words text-[15px] font-semibold text-text-1">
          {card.kind === 'goal'
            ? card.goal
            : card.kind === 'config'
              ? 'viko.project.yaml'
              : card.kind === 'lane'
                ? card.name
                : card.kind === 'task'
                  ? card.title
                  : card.finding.title}
        </span>
      </label>
      {card.kind === 'goal' ? (
        <>
          {card.milestones.length ? (
            <ol className="m-0 grid gap-1 pl-5 text-[14px] text-text-1">
              {card.milestones.map((m) => (
                <li key={m.title}>
                  {m.title}
                  {m.criteria.length ? (
                    <ul className="m-0 pl-4 text-text-2">
                      {m.criteria.map((c) => (
                        <li key={c}>{c}</li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : (
            <p className="m-0 text-[14px] text-text-3">
              No milestones yet: ask the agent for them.
            </p>
          )}
          <Evidence quote={card.evidence[0]?.quote} />
        </>
      ) : null}
      {card.kind === 'config' ? (
        <>
          {card.choices.length ? (
            <ul className="m-0 grid gap-1 pl-0 text-[14px]" aria-label="Config choices">
              {card.choices.map((c) => (
                <li key={`${c.path}:${c.value}`} className="list-none">
                  <code className="text-text-1">
                    {c.path} = {c.value}
                  </code>{' '}
                  <span className="text-text-3">
                    {c.source === 'learned' ? 'learned' : 'proposed'} · {c.why}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-[14px] text-text-3">The drafted defaults, unchanged.</p>
          )}
          <details className="text-[14px]">
            <summary className="cursor-pointer text-text-2">
              Show the file that will be committed
            </summary>
            <pre className="mt-1 max-h-72 overflow-auto rounded-lg bg-surface-2 p-2 text-[14px] text-text-1">
              {card.yaml}
            </pre>
          </details>
          <p className="m-0 text-[14px] text-text-3">
            Always included: the first machine commits exactly this file.
          </p>
        </>
      ) : null}
      {card.kind === 'lane' ? (
        <>
          {card.brief ? <p className="m-0 text-[14px] text-text-2">{card.brief}</p> : null}
          <Evidence quote={card.evidence[0]?.quote} />
        </>
      ) : null}
      {card.kind === 'task' ? (
        <>
          {card.lane ? <p className="m-0 text-[14px] text-text-2">Lane: {card.lane}</p> : null}
          <Evidence quote={card.evidence[0]?.quote} />
        </>
      ) : null}
      {card.kind === 'normalize' ? (
        <>
          {card.finding.detail ? (
            <p className="m-0 text-[14px] text-text-2">{card.finding.detail}</p>
          ) : null}
          {card.finding.paths.length ? (
            <p className="m-0 break-words text-[14px] text-text-3">
              Repository files: {card.finding.paths.join(', ')}
            </p>
          ) : null}
          <p className="m-0 text-[14px] text-text-3">
            {card.finding.via === 'migrate-config'
              ? 'Proposed through landing on its own branch.'
              : 'Becomes a task that lands through verification. Your global files are never edited.'}
          </p>
          {card.finding.patch ? (
            <details className="text-[14px]">
              <summary className="cursor-pointer text-text-2">Preview the change</summary>
              <pre className="mt-1 max-h-72 overflow-auto rounded-lg bg-surface-2 p-2 text-[14px] text-text-1">
                {card.finding.patch}
              </pre>
            </details>
          ) : null}
        </>
      ) : null}
    </li>
  )
}

export function ScopeProjectDialog({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: string
  onClose(): void
  onCreated?: (projectId: string) => void
}) {
  const b = useBackend()
  const qc = useQueryClient()
  const project = useProject(projectId).data
  const setup = project?.setup
  const { bark, store } = useBark(projectId, setup?.barkId ?? '')
  const tendencies = useTendencies().data
  const machines = useMachines().data ?? []
  const deps = useAcceptDeps()
  const [excluded, setExcluded] = useState<Set<string>>(new Set())
  const [picked, setPicked] = useState<string[] | null>(null)
  const [rows, setRows] = useState<CheckoutProgress[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const cards = useMemo(
    () =>
      buildSetupCards({
        ...(bark ? { bark } : {}),
        ...(setup?.scope ? { scope: setup.scope } : {}),
        ...(tendencies ? { tendencies } : {}),
        typedGoal: project?.goal ?? '',
        existing: setup?.repo?.kind === 'existing',
      }),
    [bark, setup, tendencies, project?.goal],
  )
  const online = machines.filter((m) => m.online)
  const author = setup?.machineId
  const chosen = picked ?? online.map((m) => m.id)
  const machineIds = author ? [author, ...chosen.filter((m) => m !== author)] : chosen
  const authorOnline = !!author && online.some((m) => m.id === author)
  const accepted = cards.filter((c) => !excluded.has(c.id))
  const created = setup?.state === 'created'
  const problem = !setup?.repo
    ? 'This project has no scoping session.'
    : !authorOnline
      ? `${machines.find((m) => m.id === author)?.name ?? 'The scoping machine'} is offline.`
      : !accepted.some((c) => c.kind === 'config')
        ? 'Waiting for the drafted config.'
        : ''

  const create = async () => {
    if (!setup?.repo || !author || problem || busy) return
    setBusy(true)
    setError('')
    try {
      const r = await acceptSetup(
        deps,
        {
          projectId,
          name: project?.name ?? '',
          typedGoal: project?.goal ?? '',
          repo: repoChoiceOf(setup.repo),
          machineIds: machineIds.filter((id) => online.some((m) => m.id === id)),
          authorMachineId: author,
          cards: accepted,
          setup,
        },
        (p) => setRows((cur) => [...cur.filter((x) => x.machineId !== p.machineId), p]),
      )
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.projects }),
        qc.invalidateQueries({ queryKey: qk.machines }),
        qc.invalidateQueries({ queryKey: ['goal'] }),
      ])
      if (r.checkouts.some(checkoutOk)) {
        onClose()
        onCreated?.(projectId)
      } else setError('No machine finished the checkout. Fix the issue above and try again.')
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={`Scope ${project?.name ?? 'project'}`}
      subtitle="The conversation and research are saved with the project. Nothing is created until you choose Create."
      placement="center"
      size="lg"
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {created ? 'Close' : 'Continue later'}
          </Button>
          {created ? null : (
            <Button
              variant="primary"
              disabled={!!problem || busy}
              title={problem || undefined}
              onClick={() => void create()}
            >
              {busy ? 'Creating…' : `Create project (${accepted.length} cards)`}
            </Button>
          )}
        </>
      }
    >
      <div className="grid gap-5 pb-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <section aria-label="Scoping conversation" className="grid content-start gap-3">
          {bark && store ? (
            // A split-off topic lives on in the project tab; scoping stays on this item.
            <Conversation bark={bark} store={store} onOpenItem={() => {}} />
          ) : (
            <p className="m-0 text-[14px] text-text-3">
              {b.entities
                ? 'Starting the conversation…'
                : 'Unlock this browser to see the conversation.'}
            </p>
          )}
        </section>
        <section aria-label="Proposed setup" className="grid content-start gap-3">
          <h3 className="m-0 flex items-center gap-2 text-[15px] font-semibold text-text-1">
            <Sparkles size={14} aria-hidden="true" /> Proposed setup
          </h3>
          <ul className="m-0 grid list-none gap-2 p-0">
            {cards.map((c) => (
              <SetupCardView
                key={c.id}
                card={c}
                included={!excluded.has(c.id)}
                disabled={busy || created}
                onToggle={(v) =>
                  setExcluded((cur) => {
                    const next = new Set(cur)
                    if (v) next.delete(c.id)
                    else next.add(c.id)
                    return next
                  })
                }
              />
            ))}
          </ul>
          {setup?.scope?.facts?.length ? (
            <details className="text-[14px]">
              <summary className="cursor-pointer text-text-2">What the audit found</summary>
              <ul className="m-0 mt-1 grid gap-0.5 pl-5 text-text-2">
                {setup.scope.facts.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </details>
          ) : null}
          {setup?.scope?.globalNotes?.length ? (
            <ul className="m-0 grid gap-1 pl-5 text-[14px] text-text-3" aria-label="Global files">
              {setup.scope.globalNotes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          ) : null}
          {tendencies?.items.length ? <TendencyList tendencies={tendencies} /> : null}
          <fieldset className="grid gap-1.5 border-0 p-0" disabled={busy || created}>
            <legend className="mb-1 text-[14px] font-semibold">Check out on</legend>
            {machines.map((m) => (
              <label key={m.id} className="flex items-center gap-2 text-[14px]">
                <input
                  type="checkbox"
                  disabled={!m.online || m.id === author}
                  checked={m.online && machineIds.includes(m.id)}
                  onChange={(e) =>
                    setPicked(
                      e.target.checked ? [...chosen, m.id] : chosen.filter((x) => x !== m.id),
                    )
                  }
                />
                <Laptop size={14} aria-hidden="true" className="text-text-3" />
                <span className="font-medium">{m.name}</span>
                {m.id === author ? (
                  <span className="text-text-3">scoped here · commits the config</span>
                ) : null}
                {m.online ? null : <span className="text-text-3">Offline</span>}
              </label>
            ))}
          </fieldset>
          <CheckoutRows rows={rows} machines={machines} creating={setup?.repo?.kind === 'create'} />
          {error ? (
            <p role="alert" className="m-0 rounded-lg bg-status-fail-soft px-3 py-2 text-[14px]">
              {error}
            </p>
          ) : problem && !created ? (
            <p className="m-0 text-[14px] text-text-3">{problem}</p>
          ) : null}
          {created ? (
            <p className="m-0 text-[14px] text-status-ok">
              Created from this scoping conversation.
            </p>
          ) : null}
        </section>
      </div>
    </Sheet>
  )
}
